import { Box, Flex, Grid, GridItem, Collapse, IconButton, Text, Tooltip } from '@chakra-ui/react';
import React, { useContext, useMemo, useCallback, useEffect, useRef, type JSX } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CMXIcon } from '@codametrix/ui-components';
import classnames from 'classnames';
import { commonEnums, gradeBenchPanelActions, resolverSelectors } from '@codametrix/ui-common';
const { FieldType, CodeAssessmentRiskCategory } = commonEnums;
import { workBench } from '@codametrix/ui-common';
import { MedicalCodeChip } from '@codametrix/ui-components';
import CodeFieldComponent from './field-components/code-field-component';
import TextInputComponent from './field-components/text-input-field-component';
import { getWorkflowForCodeType } from '../../highlight-drawer/rubric-utils';
import CodeQuestionsSection from '../code-questions-section';
import { AppliedClassNames } from './applied-class-names';
import { selectActiveCodeId } from '../active-code';
import { buildAllAnswerSummaries } from '../workflow-wizard';
import { ScoringEventsContext } from '../score-detail-panel/ScoringEventsContext';
import ScoringCategoryBadge from '../score-detail-panel/ScoringCategoryBadge';
import '../styles/index.scss';
const { getValueAtPath, getOptionsFromTree } = workBench.pathUtils;

const IconComponent: React.FC<{ field: CMxCommonApp.IconField }> = ({
  field
}) => {
  const IconComponent = field.svgIconName ? (
    <CMXIcon
      svgIconName={field.svgIconName}
      svgIconClass={field.svgIconClass}
      svgIconSize={field.svgIconSize}
      svgViewBox={field.svgIconSize}
      svgTitleText={field.svgIconName}
    />
  ) : (
    <></>
  );

  let classnames = field.classNames;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (field.key === 'gripper') {
      e.stopPropagation();
      return;
    }
    if (field.onSelect) {
      field.onSelect(field.key);
    }
  };

  return (
    <Box onClick={handleClick} cursor={field.onSelect ? 'pointer' : 'default'}>
      <AppliedClassNames classNames={field.classNames} value={IconComponent} />
    </Box>
  );
};

const SelectListFieldComponent: React.FC<{
  field: CMxCommonApp.SelectListField;
  value: object;
}> = ({ field, value }) => {
  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    field.onSelect(field.path, e.target.value, value);
  };

  let options = field['options'] ? field['options'] : field['defaultOptions'];

  if (field.optionsTree) {
    let optionsAtTree = getOptionsFromTree(
      value,
      field.optionsTree,
      field.optionsTreePath
    );

    if (optionsAtTree) {
      options = optionsAtTree;
    }
  }

  const key = field.rerenderFields ? field.rerenderFields.map((field) => value[field]).join('-') : '';

  const valueAtPath = getValueAtPath(value, field.path);
  const optionsIncludeValue = options.includes(valueAtPath);

  return (
    <select
      className="cmx-form-select"
      key={key}
      disabled={field.readonly}
      value={valueAtPath}
      onChange={handleSelect}>
      <option value="" disabled selected>
        {field.placeholder}
      </option>
      {!optionsIncludeValue && valueAtPath && (
        <option value={valueAtPath} disabled>
          {valueAtPath} (no longer available)
        </option>
      )}
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};

// Placeholder component for ButtonField
const ButtonFieldComponent: React.FC<{ field: CMxCommonApp.ButtonField }> = ({
  field
}) => (
  <button onClick={() => {}}>
    {field.icon && <span>{field.icon}</span>}
  </button>
);

type ExpandableOptions = {
  //Use css to toggle display of component
  //Should display will recieve the rows on state and render off a value in that
  shouldDisplay: (field: CMxCommonApp.CodeLayerField[]) => boolean;
  component: React.FC<any>;
};

export type GenericCodeLayerProps = {
  // Fields are similar to form fields
  fields: CMxCommonApp.CodeLayerField[];

  /*
  An item can render an additional component below it.
  This is a property of a code layer.
  Examples:
  1. An individual code can have a code search directly below it.
  2. An artifact can have evidence directly below it.
  If we add many foldouts and expansions than it could be its own layer
  */
  expandableOptions?: ExpandableOptions;
  key: string;
  // fields get their values from the values object
  values: object;
  classNames?: string[];
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries;
  /** Work item ID (t_work_item.id) for system question expression evaluation */
  workItemId?: number;
};

//Sets widths of fields using chakra grid
//React to screen size?
//Recommend 4 or less fields
//Todo : Padding fr if fr total < 10 or does muy grid automatically align center
function generateTemplateColumns(
  draggable: boolean,
  expandable: boolean,
  fieldFractions: string[]
): string {
  const calculateFractions = fieldFractions.reduce((acc, fraction) => {
    return acc + `${fraction} `;
  }, '');

  return calculateFractions;
}

function mapCategorytoSvgName(category: string) {
  if (!category) {
    return '';
  }

  switch (category) {
    case CodeAssessmentRiskCategory.CORRECT:
      return 'CheckCircle1';
    case CodeAssessmentRiskCategory.INCORRECT:
      return 'WrongCode1';
    case CodeAssessmentRiskCategory.MISSED:
      return 'PlusCircle1';
    default:
      return '';
  }
}

export function findCodeAssessmentRiskEntry(
  code: Accelerate.CQACode,
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries
) {
  if (
    !code ||
    !code.codeAssessment ||
    !code.codeType ||
    !cqaCodeAssessmentRiskDictionaries
  ) {
    return null;
  }

  const assessment = code.codeAssessment.assessment;
  const role = code.codeAssessment.role;
  const codeType = code.codeType;

  return cqaCodeAssessmentRiskDictionaries[codeType].rows.find(
    (row) => row.assessment == assessment && row.role == role
  );
}

export function updateCodeAssessmentIcon(
  field: CMxCommonApp.CodeLayerField,
  code: Accelerate.CQACode,
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries
) {
  if (field.key !== 'baseCQACodeAssessmentIcon') {
    return field;
  }

  const row = findCodeAssessmentRiskEntry(
    code,
    cqaCodeAssessmentRiskDictionaries
  );

  if (!row) {
    return field;
  }

  return {
    ...field,
    svgIconName: mapCategorytoSvgName(row.category)
  };
}

function componentForField(
  field: CMxCommonApp.CodeLayerField,
  code: object,
  cqaCodeAssessmentRiskDictionaries: Accelerate.CQACodeAssessmentRiskDictionaries,
  idealCodeIds?: Set<number> | null,
  isGraded?: boolean
) {
  if (field.hidden) {
    return null;
  }
  switch (field.fieldType) {
    case FieldType.SelectList:
      return <SelectListFieldComponent field={field} value={code} />;
    case FieldType.Button:
      return <ButtonFieldComponent field={field} />;
    case FieldType.Text:
      return (
        <AppliedClassNames
          classNames={field.classNames}
          value={getValueAtPath(code, field.key)}
        />
      );
    case FieldType.Icon:
      if (field.key === 'baseCQACodeAssessmentIcon') {
        const updatedField = updateCodeAssessmentIcon(
          field,
          code as Accelerate.CQACode,
          cqaCodeAssessmentRiskDictionaries
        );
        return <IconComponent field={updatedField} />;
      }
      return <IconComponent field={field} />;
    case FieldType.Code: {
      const classNamesMedicalCodeChip: string[] = [];
      const cqaCode = code as Accelerate.CQACode;
      if (idealCodeIds != null && isGraded) {
        if (idealCodeIds.has(cqaCode.id)) {
          classNamesMedicalCodeChip.push('final-code-accepted');
        } else {
          classNamesMedicalCodeChip.push('incorrect-medical-code');
        }
      } else if (idealCodeIds == null) {
        // Legacy fallback: assessment/role-based strikethrough
        const row = findCodeAssessmentRiskEntry(cqaCode, cqaCodeAssessmentRiskDictionaries);
        if (row && row.category == CodeAssessmentRiskCategory.INCORRECT) {
          classNamesMedicalCodeChip.push('incorrect-medical-code');
        }
      }
      // else: resolver has run (idealCodeIds != null) but code not yet graded → neutral chip
      return (
        <CodeFieldComponent
          field={field}
          value={code}
          classNamesMedicalCodeChip={classNamesMedicalCodeChip}
        />
      );
    }
    case FieldType.TextInput:
      return <TextInputComponent field={field} value={code} />;
    default:
      return null; // Handle unknown field types gracefully
  }
}

function createGridItems(
  expandable: boolean,
  fieldsComponents: JSX.Element[]
): JSX.Element[] {
  let components = [];

  const fieldGridItems = fieldsComponents.map((component) => (
    // @ts-ignore - TypeScript cannot handle complex Chakra UI GridItem union types
    (<GridItem w="100%">{component}</GridItem>)
  ));
  components = [...components, ...fieldGridItems];

  if (expandable) {
    components.push(<GridItem w="100%"> \/</GridItem>);
  }

  return components;
}

// Could also be called "Row", it is based off of fields, nothing code specific
const GenericCodeLayer: React.FC<GenericCodeLayerProps> = (
  props: GenericCodeLayerProps
) => {
  const {
    fields,
    expandableOptions,
    values,
    classNames,
    cqaCodeAssessmentRiskDictionaries,
    workItemId
  } = props;
  const _itemRef = React.useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const idealCodeIds = useSelector(resolverSelectors.selectIdealCodeIds);

  const scoringEvent = useContext(ScoringEventsContext);

  // Gate code questions behind case-level completion
  const caseLevelComplete = useSelector(
    (state: any) => state.gradeBenchPanel?.caseLevelComplete
  );
  const codeQuestionsDisabled = caseLevelComplete === false;

  // Get rubricVersion from Redux (same pattern as CaseQuestionsForm)
  const rubricVersion = useSelector(
    (state: any) => state.gradeBenchPanel?.rubricVersion
  ) as CQA.FullRubricVersion | null | undefined;
  const rubricVersionId = useSelector(
    (state: any) =>
      state.gradeBenchPanel?.qaRule?.data?.project_configuration?.rubricVersionId
  ) as number | null | undefined;

  // Get the code object from values (it includes id, codeType, attributes)
  const code = values as Accelerate.CQACode;
  const codeId = String(code.id);

  const codeCompletion = useSelector((s: any) => s.gradeBenchPanel?.codeCompletion);
  const isGraded = codeCompletion?.[codeId] === true;

  const deductionEntry = useMemo(() => {
    if (!scoringEvent || !code.code || !code.codeType) return null;
    const breakdownTypeMap: Record<string, string> = {
      procedure: 'cpt',
      diagnosis: 'icd',
      modifier: 'modifier'
    };
    const breakdownType = breakdownTypeMap[code.codeType.toLowerCase()];
    if (!breakdownType) return null;
    return (
      scoringEvent.breakdown.find(
        e => e.codeType === breakdownType && e.codeValue === code.code && e.rubricPoints > 0.001
      ) ?? { category: '0_IDEAL', rubricPoints: 0, codeType: breakdownType, answerId: -1, displayText: null }
    );
  }, [scoringEvent, code.code, code.codeType]);

  const rubricWorkflow = useMemo(() => {
    if (!rubricVersion || !code.codeType) return undefined;
    return getWorkflowForCodeType(rubricVersion, code.codeType);
  }, [rubricVersion, code.codeType]);

  // Check if rubric workflow has questions
  const hasRubricWorkflow = rubricWorkflow?.questions?.length > 0;
  const hasRubricVersion = Boolean(rubricVersion || rubricVersionId);

  // Last answered question summary — shown inline in the chip row when collapsed (CDMX-15251)
  const lastAnswerSummary = useMemo(
    () => (rubricWorkflow ? buildAllAnswerSummaries(rubricWorkflow.questions, code.attributes).at(-1) ?? null : null),
    [rubricWorkflow, code.attributes]
  );

  const wizardResetRef = useRef<(() => void) | null>(null);

  const activeCodeId = useSelector((state: any) =>
    selectActiveCodeId(state.gradeBenchPanel)
  );
  const isOpen = activeCodeId === codeId;

  const onToggle = useCallback(() => {
    if (isOpen) {
      // Collapse the active code → release any manual hold; auto resumes
      // (advances to the next incomplete code).
      dispatch(gradeBenchPanelActions.setManualActiveCode(null));
    } else {
      // Expand this code for review / jump.
      dispatch(gradeBenchPanelActions.setManualActiveCode(codeId));
    }
  }, [dispatch, isOpen, codeId]);

  const reportCompletion = useCallback(
    (complete: boolean) => {
      dispatch(gradeBenchPanelActions.setCodeCompletion({ codeId, complete }));
    },
    [dispatch, codeId]
  );

  useEffect(() => {
    if (rubricVersion && !hasRubricWorkflow) {
      dispatch(
        gradeBenchPanelActions.setCodeCompletion({ codeId, complete: true })
      );
    }
  }, [rubricVersion, hasRubricWorkflow, dispatch, codeId]);

  // Create handlers for attribute changes (same pattern as CaseQuestionsForm)
  const handleAnswerChange = useCallback(
    (params: CQA.UpdateAttributeParams) => {
      dispatch(gradeBenchPanelActions.updateAttribute(params) as any);
    },
    [dispatch]
  );

  const handleDeleteAnswer = useCallback(
    async (params: CQA.DeleteAttributeParams) => {
      await dispatch(gradeBenchPanelActions.deleteAttribute(params) as any);
    },
    [dispatch]
  );

  const handleClearDownstream = useCallback(
    (params: CQA.ClearDownstreamAttributesParams) => {
      dispatch(gradeBenchPanelActions.clearDownstreamAttributes(params));
    },
    [dispatch]
  );

  // Fields to hide when rubric workflow exists (Assessment/Role dropdowns and icon)
  const ASSESSMENT_ROLE_FIELD_KEYS = ['assessment', 'role', 'baseCQACodeAssessmentIcon'];

  const visibleFields = useMemo(() => {
    if (hasRubricVersion) {
      return fields.filter(field => !ASSESSMENT_ROLE_FIELD_KEYS.includes(field.key));
    }
    return fields;
  }, [fields, hasRubricVersion]);

  const columns = visibleFields.reduce((acc, field) => {
    let temp = acc;
    if (!field!.hidden) {
      temp.push(field.width);
    }
    return temp;
  }, []);

  const templateColumns = generateTemplateColumns(true, false, columns);

  //Get the components for each field
  const mapped = visibleFields
    .map((field) => {
      return componentForField(
        field,
        values,
        cqaCodeAssessmentRiskDictionaries,
        idealCodeIds,
        isGraded
      );
    })
    .filter((item) => item !== null);

  //Wrap component for each field
  const gridItems = createGridItems(false, mapped);



  //If expandable, add expandable component
  const displayExpandable = expandableOptions
    ? expandableOptions.shouldDisplay(fields)
    : false;
  const Component = expandableOptions?.component;
  const ExpandComponent = Component ? (
    <Component />
  ) : (
    <div>No expandable Options</div>
  );

  const classes = classnames(classNames);

  return (
    <Box ref={_itemRef}>
      <Box
        display="flex"
        alignItems="center"
        onClick={hasRubricWorkflow ? onToggle : undefined}
        cursor={hasRubricWorkflow ? 'pointer' : undefined}
      >
        {/* @ts-ignore - TypeScript cannot handle complex Chakra UI Grid union types */}
        <Grid
          flex="1"
          templateColumns={templateColumns}
          gap={0}
          alignItems={'center'}
          className={classes}>
          {gridItems}
        </Grid>
        <Flex flex={1} minWidth={0} overflow="hidden" align="center" mr={1}>
          {!isOpen && lastAnswerSummary && (
            <Text
              as="span"
              fontSize="xs"
              color="gray.500"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              <Text as="span" fontWeight="semibold" color="gray.600">{lastAnswerSummary.label}</Text>
              <Text as="span" mx="3px" color="gray.400">—</Text>
              {lastAnswerSummary.answer}
            </Text>
          )}
        </Flex>
        {deductionEntry && (
          <Box flexShrink={0} pl="6px">
            <ScoringCategoryBadge value={deductionEntry.category} score={deductionEntry.rubricPoints} />
          </Box>
        )}
        {hasRubricWorkflow && (
          lastAnswerSummary ? (
            <Tooltip label="Start over">
              <IconButton
                aria-label="Start over"
                icon={<Text fontSize="xs" lineHeight={1}>↩</Text>}
                onClick={e => {
                  e.stopPropagation();
                  wizardResetRef.current?.();
                  if (!isOpen) dispatch(gradeBenchPanelActions.setManualActiveCode(codeId));
                }}
                variant="ghost"
                size="sm"
                flexShrink={0}
              />
            </Tooltip>
          ) : (
            <Tooltip
              label="Complete case-level questions first"
              isDisabled={!codeQuestionsDisabled}
            >
              <IconButton
                aria-label={isOpen ? 'Collapse questions' : 'Expand questions'}
                icon={
                  <CMXIcon
                    svgIconName={isOpen ? 'BiomeChevronDownSVG20' : 'BiomeChevronRight'}
                    svgIconSize="16"
                  />
                }
                onClick={e => { e.stopPropagation(); onToggle(); }}
                variant="ghost"
                size="sm"
                opacity={codeQuestionsDisabled ? 0.5 : 1}
                flexShrink={0}
              />
            </Tooltip>
          )
        )}
      </Box>
      <Box display={displayExpandable ? 'block' : 'none'} className={classes}>
        {ExpandComponent}
      </Box>
      {/* Questions section BELOW the code row (collapsible) */}
      {hasRubricWorkflow && (
        <Collapse in={isOpen} animateOpacity>
          <CodeQuestionsSection
            code={code}
            rubricWorkflow={rubricWorkflow}
            onAnswerChange={handleAnswerChange}
            onDeleteAnswer={handleDeleteAnswer}
            onClearDownstream={handleClearDownstream}
            evaluateFn={gradeBenchPanelActions.evaluateWorkflow}
            workItemId={workItemId}
            isDisabled={codeQuestionsDisabled}
            isCaseIncomplete={codeQuestionsDisabled}
            onCompletionChange={reportCompletion}
            resetRef={wizardResetRef}
          />
        </Collapse>
      )}
    </Box>
  );
};

//Public Component
export default GenericCodeLayer;

//"Private" exports for unit testing
export const CodeLayerFieldComponents = {
  SelectListFieldComponent
  //Add more components as needed
};
